/**
 * CMS-15 — catalog review queue (docs/06 §2.15). One card per row that a human has to look at:
 * the Excel row as the client delivered it next to the MusicBrainz and Deezer candidates, with
 * the four decisions of the API (accept / invert / no artist / ignore).
 *
 * The queue is filled offline by `tools/catalog-identify review-queue`; filtering, ordering,
 * paging and the tab counters are all server-side (docs/06 §4). Keyboard: **A** accept,
 * **I** invert, **X** ignore on the focused card, which is what makes 1 300 rows tractable.
 */
import type {
  CmsReviewActionResponse,
  CmsReviewDTO,
  CmsReviewQueryInput,
  CmsReviewResponse,
  ReviewCandidate,
  ReviewKind,
} from '@akpl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { ExportCsvButton } from '../components/data-table/ExportCsvButton';
import { ListError } from '../components/data-table/ListError';
import { PageHeader } from '../components/layout/AppShell';
import { resultsAnchorProps, useResultsAnchor } from '../components/layout/resultsAnchor';
import { Button } from '../components/ui/button';
import { Label, Select } from '../components/ui/field';
import { Badge } from '../components/ui/panel';
import { useToast } from '../components/ui/toast';
import { useT } from '../i18n';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatNumber } from '../lib/format';

export const REVIEW_PAGE_SIZE = 25;
export const REVIEW_KINDS: ReviewKind[] = ['swap', 'match', 'placeholder'];

/** The four decisions, in the order the card renders them. */
export type ReviewAction = 'accept' | 'swap' | 'no-artist' | 'ignore';

export interface ReviewFilters {
  kind: ReviewKind;
  status: 'open' | 'all';
  sort: 'score' | 'created_at';
  dir: 'asc' | 'desc';
}

export const DEFAULT_REVIEW_FILTERS: ReviewFilters = {
  kind: 'swap',
  status: 'open',
  sort: 'score',
  dir: 'asc',
};

export function toReviewQuery(filters: ReviewFilters, page: number): CmsReviewQueryInput {
  return {
    page,
    pageSize: REVIEW_PAGE_SIZE,
    kind: filters.kind,
    status: filters.status,
    sort: filters.sort,
    dir: filters.dir,
  };
}

/** `A` / `I` / `X` (Figma CMS-15). Returns null for anything else, including a typing context. */
export function shortcutAction(key: string): ReviewAction | null {
  switch (key.toLowerCase()) {
    case 'a':
      return 'accept';
    case 'i':
      return 'swap';
    case 'x':
      return 'ignore';
    default:
      return null;
  }
}

export const reviewCsvColumns: CsvColumn<CmsReviewDTO>[] = [
  { header: 'id', value: (r) => r.id },
  { header: 'song_id', value: (r) => r.songId },
  { header: 'source_id', value: (r) => r.payload.excel.sourceId ?? '' },
  { header: 'kind', value: (r) => r.kind },
  { header: 'status', value: (r) => r.status },
  { header: 'score', value: (r) => r.score ?? '' },
  { header: 'reason', value: (r) => r.payload.reason ?? '' },
  { header: 'excel_title', value: (r) => r.payload.excel.title },
  { header: 'excel_artist', value: (r) => r.payload.excel.artist },
  { header: 'song_title', value: (r) => r.song.title },
  { header: 'song_artists', value: (r) => r.song.artists.map((a) => a.name).join(' & ') },
  { header: 'mb_title', value: (r) => r.payload.mb?.title ?? '' },
  { header: 'mb_artist', value: (r) => r.payload.mb?.artist ?? '' },
  { header: 'mbid', value: (r) => r.payload.mb?.mbid ?? '' },
  { header: 'deezer_title', value: (r) => r.payload.deezer?.title ?? '' },
  { header: 'deezer_artist', value: (r) => r.payload.deezer?.artist ?? '' },
  { header: 'deezer_id', value: (r) => r.payload.deezer?.deezerId ?? '' },
  { header: 'isrc', value: (r) => r.payload.deezer?.isrc ?? r.payload.mb?.isrc ?? '' },
  { header: 'cover_url', value: (r) => r.payload.coverUrl ?? '' },
  { header: 'swap_title', value: (r) => r.payload.swap?.title ?? '' },
  { header: 'swap_artists', value: (r) => (r.payload.swap?.artists ?? []).join(' & ') },
];

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 text-[12px]">
      <span className="w-16 shrink-0 text-muted">{label}</span>
      <span className="min-w-0 flex-1 truncate font-semibold text-ink" title={value}>
        {value}
      </span>
    </div>
  );
}

function Pane({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 flex-1 rounded-[10px] bg-canvas px-3 py-2.5">
      <p className="mb-2 text-[12px] font-semibold tracking-wide text-muted uppercase">{title}</p>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

const DASH = '—';

function CandidatePane({
  title,
  candidate,
  kind,
}: {
  title: string;
  candidate: ReviewCandidate | null;
  kind: 'mb' | 'deezer';
}) {
  const { t } = useT();
  if (!candidate) {
    return (
      <Pane title={title}>
        <p className="text-[12px] text-muted">{t('cms:review.panes.noCandidate')}</p>
      </Pane>
    );
  }
  return (
    <Pane title={title}>
      <Line
        label={t(kind === 'mb' ? 'cms:review.fields.recording' : 'cms:review.fields.title')}
        value={candidate.title ?? DASH}
      />
      <Line label={t('cms:review.fields.artist')} value={candidate.artist ?? DASH} />
      {kind === 'mb' ? (
        <Line label={t('cms:review.fields.mbid')} value={candidate.mbid ?? DASH} />
      ) : (
        <Line
          label={t('cms:review.fields.deezerId')}
          value={candidate.deezerId === null ? DASH : String(candidate.deezerId)}
        />
      )}
      {kind === 'mb' ? (
        <Line label={t('cms:review.fields.isrc')} value={candidate.isrc ?? DASH} />
      ) : (
        <Line
          label={t('cms:review.fields.cover')}
          value={candidate.coverUrl ? t('cms:review.coverAvailable') : t('cms:review.coverMissing')}
        />
      )}
    </Pane>
  );
}

const KIND_TONE = { swap: 'pink', match: 'warn', placeholder: 'cyan' } as const;

function ReviewCard({
  item,
  focused,
  canEdit,
  pending,
  resolved,
  onFocus,
  onAction,
}: {
  item: CmsReviewDTO;
  focused: boolean;
  canEdit: boolean;
  pending: ReviewAction | null;
  /**
   * The API has already accepted a decision on this row, but the list query that would drop it
   * has not answered yet. QA 2026-09-15 UX-508: the buttons used to come back to life for the
   * ~1 s between the two, on a card that was already resolved — exactly when a fast operator
   * using the A/I/X shortcuts presses again.
   */
  resolved: boolean;
  onFocus: () => void;
  onAction: (action: ReviewAction) => void;
}) {
  const { t } = useT();
  const open = item.status === 'open' && !resolved;
  const reason = item.payload.reason;
  const reasonText = reason
    ? t(`cms:review.reasons.${reason}` as 'cms:review.reasons.low_score', {
        artist: item.payload.excel.artist,
      })
    : '';

  const act = (action: ReviewAction, label: string, variant: 'primary' | 'secondary' | 'ghost') => (
    <Button
      size="sm"
      variant={variant}
      disabled={!canEdit || pending !== null || !open}
      onClick={() => onAction(action)}
    >
      {label}
    </Button>
  );

  return (
    <article
      data-testid={`review-card-${item.id}`}
      aria-current={focused ? 'true' : undefined}
      onFocus={onFocus}
      onMouseDown={onFocus}
      tabIndex={0}
      className={[
        'rounded-[12px] border bg-panel outline-none',
        focused ? 'border-pink' : 'border-line',
      ].join(' ')}
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <span className="font-mono text-[12px] font-semibold text-ink">
          {t('cms:review.sourceId', {
            id: item.payload.excel.sourceId ?? item.song.sourceId ?? '',
          })}
        </span>
        <Badge tone={KIND_TONE[item.kind]}>{t(`cms:review.tabs.${item.kind}`)}</Badge>
        <Badge tone="neutral">
          {item.score === null
            ? t('cms:review.noScore')
            : t('cms:review.score', { score: item.score.toFixed(2) })}
        </Badge>
        <span className="min-w-0 flex-1 truncate text-[12px] text-muted">{reasonText}</span>
        {open ? (
          <span className="flex shrink-0 gap-2">
            {act('accept', t('cms:review.actions.accept'), 'primary')}
            {act('swap', t('cms:review.actions.swap'), 'secondary')}
            {act('no-artist', t('cms:review.actions.noArtist'), 'secondary')}
            {act('ignore', t('cms:review.actions.ignore'), 'ghost')}
          </span>
        ) : (
          <Badge tone="ok" data-testid={`review-resolved-${item.id}`}>
            {resolved ? t('cms:review.resolved') : t(`cms:review.status.${item.status}`)}
          </Badge>
        )}
      </header>

      <div className="flex flex-wrap gap-3 p-4">
        <Pane title={t('cms:review.panes.excel')}>
          <Line label={t('cms:review.fields.title')} value={item.payload.excel.title || DASH} />
          <Line label={t('cms:review.fields.artist')} value={item.payload.excel.artist || DASH} />
          <Line
            label={t('cms:review.fields.language')}
            value={item.payload.excel.language ?? DASH}
          />
          <Line label={t('cms:review.fields.genre')} value={item.payload.excel.genre ?? DASH} />
        </Pane>
        <CandidatePane title={t('cms:review.panes.mb')} candidate={item.payload.mb} kind="mb" />
        <CandidatePane
          title={t('cms:review.panes.deezer')}
          candidate={item.payload.deezer}
          kind="deezer"
        />
      </div>

      {item.payload.swap && (
        <p className="border-t border-line px-4 py-2 text-[12px] text-muted">
          {t('cms:review.swapPreview', {
            title: item.payload.swap.title,
            artists: item.payload.swap.artists.join(' & '),
          })}
        </p>
      )}
    </article>
  );
}

/** What the operator has resolved since the screen was opened (the footer of CMS-15). */
interface SessionTally {
  accepted: number;
  swapped: number;
  noArtist: number;
  ignored: number;
}

const EMPTY_TALLY: SessionTally = { accepted: 0, swapped: 0, noArtist: 0, ignored: 0 };

/** `cms:review.actions.*` key of each decision, for the toast that says one landed. */
const ACTION_LABEL: Record<ReviewAction, 'accept' | 'swap' | 'noArtist' | 'ignore'> = {
  accept: 'accept',
  swap: 'swap',
  'no-artist': 'noArtist',
  ignore: 'ignore',
};

const TALLY_KEY: Record<ReviewAction, keyof SessionTally> = {
  accept: 'accepted',
  swap: 'swapped',
  'no-artist': 'noArtist',
  ignore: 'ignored',
};

export function CatalogReview() {
  // UX-576: /review is a list too, so it gets the «Saltar a los resultados» link.
  useResultsAnchor();
  const { t, i18n } = useT();
  const locale = i18n.language;
  const { role } = useAuth();
  const canEdit = atLeast(role, 'editor');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [filters, setFilters] = useState<ReviewFilters>(DEFAULT_REVIEW_FILTERS);
  const [page, setPage] = useState(1);
  /**
   * Rows the API has already resolved on this page. The list query is only invalidated once the
   * mutation settles, so for ~1 s the card is still on screen; it is marked "Resuelta" and its
   * buttons stay dead, and the counters are lowered by the same amount, instead of the queue
   * re-arming a decision the operator has already made (UX-508). Cleared whenever the page the
   * ids belong to changes.
   */
  const [resolvedIds, setResolvedIds] = useState<string[]>([]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [tally, setTally] = useState<SessionTally>(EMPTY_TALLY);
  const [error, setError] = useState<string | null>(null);

  const query = toReviewQuery(filters, page);
  const result = useQuery<CmsReviewResponse>({
    queryKey: ['cms', 'review', query],
    queryFn: () => api.cmsReviews({ query }),
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => result.data?.items ?? [], [result.data]);
  const counts = result.data?.counts;
  const isResolved = useCallback((id: string) => resolvedIds.includes(id), [resolvedIds]);
  const stillOpen = useMemo(
    () => items.filter((item) => item.status === 'open' && !resolvedIds.includes(item.id)),
    [items, resolvedIds],
  );

  // The focused card is the one the shortcuts act on. It is derived, not stored: when the row an
  // operator resolved leaves the page, the focus falls back to the top of the list on its own.
  // Only an unresolved row can be focused, so A/I/X can never land twice on the same decision.
  const focused = stillOpen.find((item) => item.id === focusedId) ?? stillOpen[0] ?? null;

  const resolve = useMutation<CmsReviewActionResponse, Error, { id: string; action: ReviewAction }>(
    {
      mutationFn: ({ id, action }) => {
        const params = { params: { id } };
        if (action === 'accept') return api.cmsReviewAccept(params);
        if (action === 'swap') return api.cmsReviewSwap(params);
        if (action === 'no-artist') return api.cmsReviewNoArtist(params);
        return api.cmsReviewIgnore(params);
      },
      onSuccess: (_data, variables) => {
        setTally((current) => ({
          ...current,
          [TALLY_KEY[variables.action]]: current[TALLY_KEY[variables.action]] + 1,
        }));
        setResolvedIds((current) => [...current, variables.id]);
        setError(null);
        toast({
          message: t('cms:review.resolvedToast', {
            action: t(`cms:review.actions.${ACTION_LABEL[variables.action]}`),
          }),
        });
        void queryClient.invalidateQueries({ queryKey: ['cms', 'review'] });
      },
      onError: () => setError(t('cms:review.actionFailed')),
    },
  );

  const pending = resolve.isPending ? resolve.variables : null;

  const runAction = useCallback(
    (id: string, action: ReviewAction) => {
      if (!canEdit || resolve.isPending) return;
      resolve.mutate({ id, action });
    },
    [canEdit, resolve],
  );

  // A / I / X on the focused card. Never while the operator is typing in a field.
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      const action = shortcutAction(event.key);
      if (!action || !focused || focused.status !== 'open') return;
      event.preventDefault();
      runAction(focused.id, action);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, focused, runAction]);

  const update = (patch: Partial<ReviewFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
    setResolvedIds([]);
  };

  const goToPage = (next: number) => {
    setPage(next);
    setResolvedIds([]);
  };

  // What the operator has resolved here but the server has not told us about yet (UX-508).
  const resolvedHere = items.filter((item) => resolvedIds.includes(item.id)).length;
  const lower = (value: number) => Math.max(0, value - resolvedHere);
  const total = result.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / REVIEW_PAGE_SIZE));
  const tallyTotal = tally.accepted + tally.swapped + tally.noArtist + tally.ignored;

  return (
    <>
      <PageHeader
        title={t('cms:review.title')}
        subtitle={t('cms:review.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Label htmlFor="review-sort" className="sr-only">
              {t('cms:review.sort')}
            </Label>
            <Select
              id="review-sort"
              className="w-52"
              aria-label={t('cms:review.sort')}
              value={`${filters.sort}:${filters.dir}`}
              onChange={(event) => {
                const [sort, dir] = event.target.value.split(':') as [
                  'score' | 'created_at',
                  'asc' | 'desc',
                ];
                update({ sort, dir });
              }}
            >
              <option value="score:asc">{t('cms:review.sortScoreAsc')}</option>
              <option value="score:desc">{t('cms:review.sortScoreDesc')}</option>
              <option value="created_at:desc">{t('cms:review.sortNewest')}</option>
            </Select>
            <Select
              className="w-36"
              aria-label={t('cms:review.statusFilter')}
              value={filters.status}
              onChange={(event) =>
                update({ status: event.target.value as ReviewFilters['status'] })
              }
            >
              <option value="open">{t('cms:review.status.open')}</option>
              <option value="all">{t('cms:review.statusAll')}</option>
            </Select>
            <ExportCsvButton
              filename={`akapella-review-${filters.kind}.csv`}
              columns={reviewCsvColumns}
              // The whole filtered tab, not the page on screen (CMS-22).
              rows={(onProgress) =>
                fetchAllRowsResult(
                  (exportPage, pageSize) =>
                    api.cmsReviews({ query: { ...query, page: exportPage, pageSize } }),
                  { onProgress },
                )
              }
            />
          </div>
        }
      />

      <div className="px-6 pb-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-[10px] border border-line bg-panel p-1">
            {REVIEW_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                aria-pressed={filters.kind === kind}
                onClick={() => update({ kind })}
                className={[
                  'rounded-[8px] px-3 py-1.5 text-[13px] font-semibold',
                  filters.kind === kind ? 'bg-canvas text-ink' : 'text-muted hover:text-ink',
                ].join(' ')}
              >
                {t(`cms:review.tabs.${kind}`)}{' '}
                <span className="tabular-nums text-muted">
                  {formatNumber(
                    kind === filters.kind ? lower(counts?.[kind] ?? 0) : (counts?.[kind] ?? 0),
                    locale,
                  )}
                </span>
              </button>
            ))}
          </div>
          <span className="text-[12px] text-muted" data-testid="review-pending">
            {t('cms:review.pending', {
              total: formatNumber(lower(counts?.total ?? 0), locale),
              catalog: formatNumber(counts?.catalogTotal ?? 0, locale),
            })}
          </span>
          <span className="ml-auto text-[12px] font-semibold text-pink">
            {t('cms:review.shortcuts')}
          </span>
        </div>

        {error && (
          <p className="mb-3 text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}

        <section className="flex flex-col gap-3" {...resultsAnchorProps(t('cms:review.title'))}>
          {result.isError && (
            <ListError
              onRetry={() => void result.refetch()}
              className="rounded-[12px] border border-line bg-panel py-8"
            />
          )}
          {!result.isError &&
            items.map((item) => (
              <ReviewCard
                key={item.id}
                item={item}
                focused={focused?.id === item.id}
                canEdit={canEdit}
                pending={pending?.id === item.id ? pending.action : null}
                resolved={isResolved(item.id)}
                onFocus={() => setFocusedId(item.id)}
                onAction={(action) => runAction(item.id, action)}
              />
            ))}
          {items.length === 0 && !result.isLoading && !result.isError && (
            <p className="rounded-[12px] border border-line bg-panel px-4 py-8 text-center text-[13px] text-muted">
              {t('cms:review.empty')}
            </p>
          )}
        </section>

        <footer className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-[12px] text-muted" data-testid="review-tally">
            {t('cms:review.session', {
              total: tallyTotal,
              accepted: tally.accepted,
              swapped: tally.swapped,
              noArtist: tally.noArtist,
              ignored: tally.ignored,
            })}
          </span>
          <span className="ml-auto flex items-center gap-2">
            <span className="text-[12px] text-muted">{t('cms:table.page', { page, pages })}</span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => goToPage(Math.max(1, page - 1))}
            >
              {t('cms:table.prev')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= pages}
              onClick={() => goToPage(page + 1)}
            >
              {t('cms:table.next')}
            </Button>
          </span>
        </footer>
      </div>
    </>
  );
}
